import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Home, Search, Download, Printer } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface CounterWiseItem {
  counter_id: string;
  counter_name: string;
  total_transactions: number;
  total_collection: number;
  cash_collection: number;
  card_collection: number;
  operator_name: string;
}

export default function CounterWiseReport() {
  const { formatCurrency } = useCurrency();
  const [reportData, setReportData] = useState<CounterWiseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadCounterWiseReport();
  }, [startDate, endDate]);

  const loadCounterWiseReport = async () => {
    try {
      setLoading(true);
      
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        setReportData(getMockCounterWiseReport());
        setLoading(false);
        return;
      }

      let query = supabase
        .from('payments')
        .select(`
          id,
          amount,
          payment_method,
          payment_date,
          counter_id,
          created_by
        `)
        .not('counter_id', 'is', null);

      if (startDate) {
        query = query.gte('payment_date', startDate);
      }
      if (endDate) {
        query = query.lte('payment_date', endDate);
      }

      const { data, error } = await query;
      if (error) throw error;

      const grouped = (data || []).reduce((acc: Record<string, any>, item) => {
        const counterId = item.counter_id;
        if (!acc[counterId]) {
          acc[counterId] = {
            counter_id: counterId,
            counter_name: `Counter ${counterId}`,
            total_transactions: 0,
            total_collection: 0,
            cash_collection: 0,
            card_collection: 0,
            operator_name: item.created_by || 'N/A',
          };
        }
        
        acc[counterId].total_transactions += 1;
        acc[counterId].total_collection += item.amount;
        if (item.payment_method === 'cash') {
          acc[counterId].cash_collection += item.amount;
        } else if (item.payment_method === 'card') {
          acc[counterId].card_collection += item.amount;
        }
        
        return acc;
      }, {});

      setReportData(Object.values(grouped));
    } catch (error) {
      console.error('Error loading counter wise report:', error);
      setReportData(getMockCounterWiseReport());
    } finally {
      setLoading(false);
    }
  };

  const getMockCounterWiseReport = (): CounterWiseItem[] => {
    return [
      {
        counter_id: 'C1',
        counter_name: 'Counter 1',
        total_transactions: 125,
        total_collection: 625000,
        cash_collection: 425000,
        card_collection: 200000,
        operator_name: 'Receptionist 1',
      },
      {
        counter_id: 'C2',
        counter_name: 'Counter 2',
        total_transactions: 98,
        total_collection: 490000,
        cash_collection: 290000,
        card_collection: 200000,
        operator_name: 'Receptionist 2',
      },
      {
        counter_id: 'C3',
        counter_name: 'Counter 3',
        total_transactions: 110,
        total_collection: 550000,
        cash_collection: 350000,
        card_collection: 200000,
        operator_name: 'Receptionist 3',
      },
    ];
  };

  const filteredData = reportData.filter(item => {
    const matchesSearch = 
      item.counter_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.operator_name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalCollection = filteredData.reduce((sum, item) => sum + item.total_collection, 0);
  const totalTransactions = filteredData.reduce((sum, item) => sum + item.total_transactions, 0);
  const totalCash = filteredData.reduce((sum, item) => sum + item.cash_collection, 0);

  const handleExportCSV = () => {
    const headers = ['Counter', 'Operator', 'Total Transactions', 'Total Collection', 'Cash Collection', 'Card Collection'];
    const rows = filteredData.map(item => [
      item.counter_name,
      item.operator_name,
      item.total_transactions,
      item.total_collection,
      item.cash_collection,
      item.card_collection,
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `counter-wise-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Home className="w-4 h-4" />
            <span>/</span>
            <span>Billings/Reception</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Counter Wise Report</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportCSV}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-none px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Counters</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{filteredData.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Transactions</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">{totalTransactions}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Collection</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalCollection)}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search counter, operator..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Counter
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Operator
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transactions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Collection
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cash
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Card
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      No counter data found
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item) => (
                    <tr key={item.counter_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.counter_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.operator_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.total_transactions}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-bold">
                        {formatCurrency(item.total_collection)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(item.cash_collection)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(item.card_collection)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
